import { MapSSO } from '../map-sso';
import mitt from 'mitt';
import { Logger } from '../lib/logger';
import { LoggerNames } from '../lib/constants';
import { AccessToken, IdToken, RefreshToken } from '../token-manager/token-manager.types';

export type TransformAuthStateFn<TAuthState extends AuthState = AuthState> = (
  sdk: MapSSO<TAuthState>,
  authState: AuthState | null,
) => TAuthState;

export interface IAuthStateManager<TAuthState extends AuthState = AuthState> {
  getAuthState(): TAuthState | null;
  getPreviousAuthState(): TAuthState | null;
  updateAuthState(): Promise<TAuthState | null>;
  subscribe<TEvent extends keyof AuthStateEvents>(event: TEvent, handler: AuthEventHandler<TEvent>): void;
  unsubscribe<TEvent extends keyof AuthStateEvents>(event: TEvent, handler: AuthEventHandler<TEvent>): void;
}

export interface AuthState {
  isAuthenticated: boolean;
  accessToken?: AccessToken;
  idToken?: IdToken;
  refreshToken?: RefreshToken;
  error?: any;
}

type AuthStateEvents<TAuthState extends AuthState = AuthState> = {
  authStateChange: TAuthState | null;
};

type AuthEventHandler<TEvent extends keyof AuthStateEvents, TAuthState extends AuthState = AuthState> = (
  payload: AuthStateEvents<TAuthState>[TEvent],
) => void;

type AuthStateManagerConfig<TAuthState extends AuthState> = {
  transformAuthState?: TransformAuthStateFn<TAuthState>;
  sdk: MapSSO<TAuthState>;
};

export class AuthStateManager<TAuthState extends AuthState = AuthState> implements IAuthStateManager<TAuthState> {
  private readonly logger = Logger.createLogger(LoggerNames.AUTH_STATE_MANAGER);
  private emitter = mitt<AuthStateEvents<TAuthState>>();
  private readonly transformAuthState?: TransformAuthStateFn<TAuthState>;
  private readonly sdk: MapSSO<TAuthState>;
  private authState: TAuthState | null = null;
  private previousAuthState: TAuthState | null = null;

  constructor({ transformAuthState, sdk }: AuthStateManagerConfig<TAuthState>) {
    this.sdk = sdk;
    this.transformAuthState = transformAuthState;
  }

  /**
   * Returns the current auth state. If the auth state has not been initialized yet, returns null.
   * Note that the auth state is updated asynchronously, so calls to update the auth state will need
   * to be awaited prior to this value changing.
   */
  getAuthState(): TAuthState | null {
    return this.authState;
  }

  /**
   * Returns the previous auth state. If the auth state has not been initialized yet, returns null.
   */
  getPreviousAuthState(): TAuthState | null {
    return this.previousAuthState;
  }

  /**
   * Subscribe to auth state change events. Handlers will be called with an sdk instance and the changed auth state
   * @param event
   * @param handler
   */
  subscribe<TEvent extends keyof AuthStateEvents>(event: TEvent, handler: AuthEventHandler<TEvent, TAuthState>): void {
    this.emitter.on(event, handler);
  }

  /**
   * Unsubscribe to auth state change events. Must provide the original handler passed to subscribe()
   * @param event
   * @param handler
   */
  unsubscribe<TEvent extends keyof AuthStateEvents>(
    event: TEvent,
    handler: AuthEventHandler<TEvent, TAuthState>,
  ): void {
    this.emitter.off(event, handler);
  }

  /**
   * Asynchronously updates the auth state and emits an event with the new auth state.
   * Will also resolve with the new auth state.
   *
   * If a transformAuthState function was provided, the auth state will be passed to that function before being emitted. This allows you to add custom properties, or change the existing values
   * of the auth state before they are emitted.
   *
   * If auth states are the same, no changes will be emitted. Please note that comparison does not include any custom properties added by the transformAuthState function.
   */
  async updateAuthState(): Promise<TAuthState | null> {
    this.logger.debug('Starting auth state update');
    const { accessToken, refreshToken, idToken } = this.sdk.tokenManager.getTokens() ?? {};
    const accessTokenExpired = this.sdk.tokenManager.hasExpired(accessToken);
    const idTokenExpired = this.sdk.tokenManager.hasExpired(idToken);

    try {
      const authState: AuthState = {
        isAuthenticated: !accessTokenExpired && !idTokenExpired,
        accessToken,
        refreshToken,
        idToken,
      };
      // Apply the transform function to the auth state if provided
      const transformedAuthState: TAuthState = await (this.transformAuthState
        ? this.transformAuthState(this.sdk, authState)
        : Promise.resolve(authState as TAuthState));

      this.emitAuthStateChange(transformedAuthState);
      return transformedAuthState;
    } catch (e) {
      this.logger.error('Could not transform auth state', e);
      const authState: AuthState = {
        isAuthenticated: false,
        error: e,
      };
      this.emitAuthStateChange(authState);
      return authState as TAuthState;
    }
  }

  /**
   * Determines whether an auth state change should be emitted.
   * If the state has changed, updates the authState and previousAuthState values
   * @param newAuthState
   * @private
   */
  private emitAuthStateChange(newAuthState: AuthState | null) {
    if (compareAuthState(this.authState, newAuthState)) {
      this.logger.debug('Auth state unchanged');
      return;
    }
    this.previousAuthState = this.authState;
    this.authState = newAuthState as TAuthState;
    this.logger.debug('Emitting authStateChange event', newAuthState);
    this.emitter.emit('authStateChange', newAuthState as TAuthState);
  }
}

/**
 * Compares two auth states to determine if they are the same.
 * Ignores any custom keys and only checks the known auth state properties.
 * @param prevAuthState
 * @param newAuthState
 */
function compareAuthState(prevAuthState: AuthState | null, newAuthState: AuthState | null) {
  if (prevAuthState === null) return false;

  return (
    prevAuthState.isAuthenticated === newAuthState?.isAuthenticated &&
    prevAuthState.accessToken === newAuthState?.accessToken &&
    prevAuthState.idToken === newAuthState?.idToken &&
    prevAuthState.error === newAuthState?.error
  );
}
